// dashboard.slice.ts
import { createSlice, createAsyncThunk, PayloadAction } from '@reduxjs/toolkit';
import axios from 'axios';
import { TimeRange } from './timeRange';
import { IQueryParams } from 'app/shared/reducers/reducer.utils';
import { IDashboard, IServerEntity } from './utils/utils';
const apiUrl = 'dashb/api/dashboards';
const shareUrl = 'dashb/api/dashboard-shares/consolidated';

interface DashboardState {
  loading: boolean;
  errorMessage: string | null;
  dashboards: IDashboard[];
  entity: IDashboard | null;
  updating: boolean;
  updateSuccess: boolean;
  timeRange: TimeRange;
}

const initialState: DashboardState = {
  loading: false,
  errorMessage: null,
  dashboards: [],
  entity: null,
  updating: false,
  updateSuccess: false,
  timeRange: '-1h',
};

export const getDashboards = createAsyncThunk('dashboard/fetch_list', async ({ page, size, sort, filterUrl }: IQueryParams) => {
  const response = await axios.get<IDashboard[]>(`${apiUrl}?cacheBuster=${new Date().getTime()}`);
  return response.data;
});

export const getDashboard = createAsyncThunk('dashboard/fetch_one', async ({ id, params }: { id: number | string; params?: string }) => {
  const url = params ? `${apiUrl}/${id}?${params}` : `${apiUrl}/${id}`;
  const response = await axios.get<IDashboard>(url);
  return response.data;
});

export const createDashboard = createAsyncThunk('dashboard/create', async (entity: IServerEntity) => {
  const response = await axios.post<IDashboard>(apiUrl, entity);
  return response.data;
});

export const saveDashboard = createAsyncThunk('dashboard/update', async (entity: IServerEntity) => {
  const response = await axios.put<IDashboard>(apiUrl, entity);
  return response.data;
});

export const deleteDashboard = createAsyncThunk('dashboard/delete', async (id: number | string, { dispatch }) => {
  await axios.delete(`${apiUrl}/${id}`);
  dispatch(getDashboards({}));
  return id;
});

export const saveSharedDetails = createAsyncThunk('dashboard/share', async (payload: any) => {
  const response = await axios.post<IDashboard>(shareUrl, payload);
  return response.data;
});

const dashboardSlice = createSlice({
  name: 'dashboard',
  initialState,
  reducers: {
    reset(state) {
      Object.assign(state, { ...initialState, timeRange: state.timeRange });
    },
    resetDashboard(state) {
      state.entity = null;
    },
    setTimeRange(state, action: PayloadAction<TimeRange>) {
      state.timeRange = action.payload;
    },
  },
  extraReducers: builder => {
    builder
      .addCase(getDashboards.pending, state => {
        state.loading = true;
        state.errorMessage = null;
      })
      .addCase(getDashboards.fulfilled, (state, action) => {
        state.loading = false;
        state.dashboards = Array.isArray(action.payload) ? action.payload : [];
      })
      .addCase(getDashboards.rejected, (state, action) => {
        state.loading = false;
        state.errorMessage = action.error.message ?? 'Error loading dashboards';
      })
      .addCase(saveDashboard.rejected, (state, action) => {
        state.loading = false;
        state.errorMessage = action.error.message ?? 'Error saving dashboard';
      })
      .addCase(getDashboard.fulfilled, (state, action) => {
        state.entity = action.payload;
        state.loading = false;
      })

      .addCase(createDashboard.fulfilled, (state, action) => {
        state.entity = action.payload;
        state.dashboards.push(action.payload);
        state.updateSuccess = true;
        state.updating = false;
      })

      .addCase(saveDashboard.fulfilled, state => {
        state.updateSuccess = true;
        state.updating = false;
      })

      .addCase(deleteDashboard.fulfilled, (state, action) => {
        state.dashboards = state.dashboards.filter(d => d.id !== action.payload);
        state.entity = null;
        state.updateSuccess = true;
      })

      .addCase(saveSharedDetails.fulfilled, state => {
        state.updateSuccess = true;
      });
  },
});

export const { reset, resetDashboard, setTimeRange } = dashboardSlice.actions;

export default dashboardSlice.reducer;
